// --- Questionnaire IDs ---

/** Active backend ID used for fetching the addendum questionnaire. */
export const ADDENDUM_QUESTIONNAIRE_ID = 'rd-consult-chart-addendum';

/** All recognised addendum questionnaire IDs (current + future rename). */
export const ADDENDUM_QUESTIONNAIRE_IDS: ReadonlyArray<string> = ['rd-consult-chart-addendum'];

/** All recognised RD consult chart questionnaire IDs (production + mock). */
export const RD_CONSULT_CHART_IDS: ReadonlyArray<string> = ['rd-consult-chart-standardized-2026'];

// --- Extension URLs ---

export const EXT_ORIGINAL_CHART = 'https://projectwell.io/fhir/extensions/original-chart';
export const EXT_CHAIN_PARENT = 'https://projectwell.io/fhir/extensions/chain-parent';
/** Legacy extension URL — kept for backward-compat reads. */
export const PARENT_QR_EXTENSION_URL = 'https://projectwell.io/fhir/extensions/parent-questionnaire-response';
export const EXT_SUBMITTED_FOR_COSIGN = 'https://projectwell.io/fhir/extensions/submitted-for-cosign';

// --- Identifier URLs ---

export const ID_SYSTEM_LAST_MODIFIED_BY = 'https://projectwell.io/fhir/identifiers/last-modified-by';
export const NPI_SYSTEM = 'http://hl7.org/fhir/sid/us-npi';

// --- Co-Sign LinkIds ---

/** LinkIds for the main RD consult chart co-sign group. */
export const COSIGN_LINK_IDS = {
  group: 'COSIGN',
  practitioner: 'COSIGN_PRACTITIONER',
  attestation: 'PW-RD-COSIGN-01',
} as const;

/**
 * Credentials answer (e.g. RDN) appended after the signer name on RD chart / addendum
 * electronic-signature lines in PDF and UI.
 */
export const RD_SIGNATURE_CREDENTIALS_LINK_ID = 'PW-RD-CREDENTIALS-01';

/** LinkIds for the addendum co-sign group. */
export const ADDENDUM_COSIGN_LINK_IDS = {
  group: 'ADDENDUM_COSIGN',
  practitioner: 'ADDENDUM_COSIGN_PRACTITIONER',
  attestation: 'ADDENDUM_COSIGN_01',
} as const;

// --- Addendum Field LinkIds ---

export const ADDENDUM_FIELD_IDS = {
  metadata: 'ADDENDUM_METADATA',
  author: 'ADDENDUM_AUTHOR',
  date: 'ADDENDUM_DATE',
  signature: 'ADDENDUM_SIGNATURE',
  dateSigned: 'ADDENDUM_DATE_SIGNED',
  signatureGroup: 'ADDENDUM_SIGNATURE_GROUP',
  attestation: 'ADDENDUM_ATTESTATION_01',
} as const;

/** All co-sign practitioner linkIds (chart + addendum). */
export const ALL_COSIGN_PRACTITIONER_IDS: ReadonlyArray<string> = [
  COSIGN_LINK_IDS.practitioner,
  ADDENDUM_COSIGN_LINK_IDS.practitioner,
];

/** All co-sign attestation linkIds (chart + addendum). */
export const ALL_COSIGN_ATTESTATION_IDS: ReadonlyArray<string> = [
  COSIGN_LINK_IDS.attestation,
  ADDENDUM_COSIGN_LINK_IDS.attestation,
];

/** All co-sign group linkIds (chart + addendum). */
export const ALL_COSIGN_GROUP_IDS: ReadonlyArray<string> = [COSIGN_LINK_IDS.group, ADDENDUM_COSIGN_LINK_IDS.group];

/** Chart groups excluded when injecting into addendums or displaying the chart copy. */
export const EXCLUDED_CHART_GROUPS: ReadonlySet<string> = new Set([COSIGN_LINK_IDS.group, 'SIGNATURE']);

/**
 * Questionnaire group or item linkIds omitted from patient survey PDF downloads
 * (UI helper / display-only copies; not exported).
 */
export const SURVEYS_PDF_EXCLUDED_LINK_IDS: ReadonlySet<string> = new Set([
  'ANTHROPOMETRICS_HELPER_DISPLAY',
  'LABS_CLINICAL_HELPER_DISPLAY',
  'LABS_OTHER_EXAMPLE_DISPLAY',
  'MEDICATIONS_HELPER_DISPLAY',
  'SDOH_BARRIERS_HELPER_DISPLAY',
  'KEY_BARRIERS_TEXT_EXAMPLE_DISPLAY',
  'INTEGRATED_CARE_PLAN_OTHER_EXAMPLE_DISPLAY',
  'ASSESSMENT_NARRATIVE_HELPER_DISPLAY',
  'PRIMARY_DIAGNOSIS_HELPER_DISPLAY',
  'ADDITIONAL_DIAGNOSES_HELPER_DISPLAY',
  'ETIOLOGY_HELPER_DISPLAY',
  'NUTRITION_INTERVENTION_HELPER_DISPLAY',
  'OUTCOMES_TO_MONITOR_HELPER_DISPLAY',
  'FOLLOWUP_TIMEFRAME_HELPER_DISPLAY',
  'NEXT_STEPS_HELPER_DISPLAY',
  'PW-RD-BILL-CC04',
]);
